import type ePub from 'epubjs';
// The pdf.js worker, bundled as a same-origin asset (vite emits it). No CDN, so PDF parsing works
// offline; the worker file ships with the app.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { token } from '@/infrastructure/container';
import { type Book, type BookChapter } from '@/domain/book';
import { rechunkChapters } from '@/domain/chunk';
import { detectChapterHeading } from '@/domain/pdf';
import { parseMarkdown } from '@/domain/markdown';
import { parsePlainText } from '@/domain/plaintext';
import { ValidationError, ServerError } from '@/domain/errors';
import { type ILogger } from './Logger';

export interface IBookParser {
  /** Parse an uploaded EPUB, PDF, Markdown, or text file into a `Book`. Throws `ValidationError`
      for an unsupported file type. */
  parse(file: File): Promise<Book>;
}

export const TOKEN_IBookParser = token<IBookParser>('IBookParser');

const PDF_NEW_PARAGRAPH_GAP = 2;

function stripExtension(name: string): string {
  return name.replace(/\.[^.]+$/, '') || 'Untitled';
}

/** Last path segment of an href, minus any `#fragment`/`?query`. Used to match a spine section
    against a TOC entry: the two often differ in path prefix (`Text/x.htm` vs `OEBPS/Text/x.htm`)
    and the TOC side carries an anchor, but the filename is stable across both. */
function fileKey(href: string): string {
  const path = href.split('#')[0].split('?')[0];
  const segments = path.split('/');
  return segments[segments.length - 1];
}

/** The subset of an epubjs `Section` this parser touches. Declared locally so we don't depend on
    epubjs's deep type paths. */
interface EpubSection {
  href: string;
  load(request?: unknown): unknown;
  unload(): void;
  document?: Document;
}

export class BookParser implements IBookParser {
  constructor(private logger: ILogger) {}

  async parse(file: File): Promise<Book> {
    const book = await this.parseRaw(file);
    // Re-pack every chapter into larger, sentence-aligned chunks so the synth never pauses
    // mid-sentence and fragments (common in PDFs) are rejoined.
    const chapters = rechunkChapters(book.chapters);
    // Re-assert non-empty: rechunking can drop chapters that were only punctuation/whitespace,
    // which the per-format guards in parseRaw wouldn't have caught.
    if (chapters.length === 0) throw new ServerError('This file has no readable text.');
    return { ...book, chapters };
  }

  private async parseRaw(file: File): Promise<Book> {
    const name = file.name.toLowerCase();
    if (name.endsWith('.epub')) return this.parseEpub(file);
    if (name.endsWith('.pdf')) return this.parsePdf(file);
    if (name.endsWith('.md') || name.endsWith('.markdown')) return this.parseMarkdownFile(file);
    if (name.endsWith('.txt')) return this.parseTextFile(file);
    throw new ValidationError('file', 'Unsupported file. Upload an EPUB, PDF, Markdown, or text file.');
  }

  private async parseMarkdownFile(file: File): Promise<Book> {
    const { title, chapters } = parseMarkdown(await file.text());
    if (chapters.length === 0) throw new ServerError('This Markdown file has no readable text.');
    return { title: title || stripExtension(file.name), format: 'md', chapters };
  }

  private async parseTextFile(file: File): Promise<Book> {
    const fallback = stripExtension(file.name);
    const chapters = parsePlainText(await file.text(), fallback);
    if (chapters.length === 0) throw new ServerError('This text file is empty.');
    return { title: fallback, format: 'txt', chapters };
  }

  private async parseEpub(file: File): Promise<Book> {
    try {
      // epubjs is large and only needed when importing a book, so load it on demand.
      const { default: ePub } = await import('epubjs');
      const book = ePub(await file.arrayBuffer());
      await book.ready;
      const metadata = await book.loaded.metadata;
      const navigation = await book.loaded.navigation;
      const title = metadata.title || file.name.replace(/\.epub$/i, '') || 'Untitled';

      // The spine is the authoritative linear reading order. The TOC (NCX/nav) is only a navigation
      // aid and frequently omits files — e.g. this 7-book Foundation omnibus lists the later volumes'
      // part dividers but not their ~100 chapter files, so a TOC-driven read silently skips whole
      // books. Walk the spine so no text is dropped, and borrow the TOC's labels where it has them.
      const tocLabels = this.tocLabelsByFile(navigation.toc);
      const sections: EpubSection[] = [];
      book.spine.each((section: EpubSection) => sections.push(section));

      const chapters: BookChapter[] = [];
      for (const section of sections) {
        const { paragraphs, heading } = await this.readEpubSection(book, section);
        if (paragraphs.length === 0) continue;
        const label =
          tocLabels.get(fileKey(section.href)) || heading || `Section ${chapters.length + 1}`;
        chapters.push({ href: section.href, label, paragraphs });
      }
      if (chapters.length === 0) throw new ServerError('EPUB contains no readable text');
      return { title, format: 'epub', language: metadata.language || undefined, chapters };
    } catch (error) {
      if (error instanceof ServerError) throw error;
      this.logger.error('BookParser.parseEpub', error);
      throw new ServerError('Could not read this EPUB. It may be corrupt or DRM-protected.', {
        cause: error,
      });
    }
  }

  /** Map each TOC entry's target file to its label, so a spine section can recover a human-friendly
      name. Keyed by filename (see `fileKey`); the first label wins when several anchors share a file. */
  private tocLabelsByFile(
    toc: readonly { label: string; href: string; subitems?: unknown }[],
  ): Map<string, string> {
    const labels = new Map<string, string>();
    const walk = (items: typeof toc): void => {
      for (const item of items) {
        const key = fileKey(item.href);
        const label = item.label.trim();
        if (label && !labels.has(key)) labels.set(key, label);
        const subitems = item.subitems as typeof toc | undefined;
        if (subitems && subitems.length > 0) walk(subitems);
      }
    };
    walk(toc);
    return labels;
  }

  private async readEpubSection(
    book: ReturnType<typeof ePub>,
    section: EpubSection,
  ): Promise<{ paragraphs: string[]; heading?: string }> {
    try {
      // epubjs's types declare `load(): Document`, but it actually resolves a Promise<Document>.
      const doc = (await (section.load(book.load.bind(book)) as unknown as Promise<Document>)) ??
        section.document;
      if (!doc) return { paragraphs: [] };
      const paragraphs: string[] = [];
      doc.querySelectorAll('p').forEach((p) => {
        const text = p.textContent?.trim();
        if (text) paragraphs.push(text);
      });
      const heading = doc.querySelector('h1, h2, h3, h4, h5, h6')?.textContent?.trim() || undefined;
      section.unload();
      return { paragraphs, heading };
    } catch (error) {
      this.logger.warn('BookParser: skipped unreadable EPUB section', section.href, error);
      return { paragraphs: [] };
    }
  }

  private async parsePdf(file: File): Promise<Book> {
    try {
      // pdf.js loads on demand; playback never needs it. The worker is the bundled local asset.
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
      const chapters: BookChapter[] = [];
      let current: BookChapter | null = null;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const paragraphs = this.extractPdfParagraphs(await page.getTextContent());
        if (paragraphs.length === 0) continue;

        const heading = detectChapterHeading(paragraphs[0]);
        if (heading) {
          if (current) chapters.push(current);
          current = { href: `page-${pageNum}`, label: heading, paragraphs: paragraphs.slice(1) };
        } else if (!current) {
          current = { href: `page-${pageNum}`, label: `Page ${pageNum}`, paragraphs };
        } else {
          current.paragraphs.push(...paragraphs);
        }
      }
      if (current && current.paragraphs.length > 0) chapters.push(current);
      if (chapters.length === 0) throw new ServerError('PDF contains no readable text');

      const title = file.name.replace(/\.pdf$/i, '') || 'Untitled';
      return { title, format: 'pdf', chapters };
    } catch (error) {
      if (error instanceof ServerError) throw error;
      this.logger.error('BookParser.parsePdf', error);
      throw new ServerError('Could not read this PDF. It may be image-only or corrupt.', {
        cause: error,
      });
    }
  }

  private extractPdfParagraphs(textContent: { items: unknown[] }): string[] {
    const paragraphs: string[] = [];
    let buffer: string[] = [];
    let lastY = 0;

    const flush = (): void => {
      const text = buffer.join(' ').trim();
      if (text) paragraphs.push(text);
      buffer = [];
    };

    for (const raw of textContent.items) {
      if (!raw || typeof raw !== 'object' || !('str' in raw)) continue;
      const item = raw as { str: string; transform: number[] };
      const text = item.str.trim();
      if (!text) continue;
      const y = item.transform[5];
      if (buffer.length > 0 && Math.abs(y - lastY) > PDF_NEW_PARAGRAPH_GAP) flush();
      buffer.push(text);
      lastY = y;
    }
    flush();
    return paragraphs;
  }
}

/** Test/headless double: returns whatever book it was constructed with, ignoring the file. */
export class FakeBookParser implements IBookParser {
  constructor(private book: Book) {}
  parse(): Promise<Book> {
    return Promise.resolve(this.book);
  }
}
